// Display source bytes once, while retaining every original timing mark/index.
// Null means contradictory/unverifiable word ranges: show only the current
// actual phrase instead of guessing a document or deduplicating by spelling.
export function buildScannedReaderTranscript(marks) {
    if (!Array.isArray(marks) || marks.length > 20000) return null;
    const encoder = new TextEncoder(), decoder = new TextDecoder('utf-8', { fatal: true });
    const events = new Map();
    let wordIndex = 0, totalBytes = 0;
    const event = position => {
        if (!events.has(position)) events.set(position, { add: [], remove: [] });
        return events.get(position);
    };
    for (const mark of marks) {
        if (!mark || !['word', 'sentence'].includes(mark.type)) continue;
        const index = mark.type === 'word' ? wordIndex++ : null;
        const bytes = typeof mark.value === 'string' ? encoder.encode(mark.value) : null;
        const valid = bytes?.length && Number.isSafeInteger(mark.start) && Number.isSafeInteger(mark.end)
            && mark.start >= 0 && mark.end > mark.start && mark.end - mark.start === bytes.length
            && decoder.decode(bytes) === mark.value;
        if (!valid) { if (index !== null) return null; else continue; }
        totalBytes += bytes.length;
        if (totalBytes > 4 * 1024 * 1024) return null;
        const source = { start: mark.start, end: mark.end, bytes, index };
        event(source.start).add.push(source); event(source.end).remove.push(source);
    }
    if (!wordIndex) return [];
    const boundaries = [...events.keys()].sort((a, b) => a - b), active = new Set(), segments = [];
    let previousEnd = null;
    try {
        for (let i = 0; i < boundaries.length - 1; i++) {
            const start = boundaries[i], end = boundaries[i + 1], change = events.get(start);
            change.remove.forEach(source => active.delete(source));
            change.add.forEach(source => active.add(source));
            if (!active.size) continue;
            const reference = active.values().next().value;
            const bytes = reference.bytes.subarray(start - reference.start, end - reference.start);
            const markIndices = [];
            for (const source of active) {
                const candidate = source.bytes.subarray(start - source.start, end - source.start);
                if (candidate.length !== bytes.length || candidate.some((byte, j) => byte !== bytes[j])) return null;
                if (source.index !== null) markIndices.push(source.index);
            }
            // Boundaries must also be UTF-8 character boundaries in every source.
            const value = decoder.decode(bytes);
            segments.push({ start, end, value, markIndices,
                gapBefore: previousEnd !== null && start > previousEnd });
            previousEnd = end;
        }
    } catch { return null; }
    return segments;
}
